import { css, html } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";
import { Style } from "./Styles";
import { BaseElement } from "./BaseElement";
import { ILocation } from "../models/ILocation";
import SlDialog from "@shoelace-style/shoelace/dist/components/dialog/dialog";
import { makeCustomEvent } from "../Utils";
import { litIf } from "./LitUtils";
import { watch } from "./internal/watch";

import "@shoelace-style/shoelace/dist/components/dialog/dialog.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/input/input.js";
import "@shoelace-style/shoelace/dist/components/select/select.js";
import "@shoelace-style/shoelace/dist/components/option/option.js";

export type DegreesMinutesSeconds = {
    degrees: number;
    minutes: number;
    seconds: number;
};

export function dmsToDecimalDegrees(d: number, m: number, s: number) {
    if (Math.sign(d) === -1) {
        return -(-d + m / 60 + s / 3600);
    }
    return d + m / 60 + s / 3600;
}

export function getInputValueAsNumber(input?: HTMLInputElement) {
    if (input && input.value) {
        const val = Number(input.value);
        if (!Number.isNaN(val)) {
            return val;
        }
    }
    return undefined;
}

@customElement("ds-dms-entry")
export class DmsEntry extends BaseElement {
    static override styles = [
        Style,
        css`
            .container {
                display: flex;
                flex-direction: row;
                align-items: center;
                width: 100%;
                height: 100%;
                gap: 0.25em;
            }

            .entry {
                display: flex;
                flex-direction: column;
                gap: var(--flex-gap);
                min-width: 0px;
            }

            .label {
                display: flex;
                align-items: center;
                min-width: 2em;
                font-variant: all-petite-caps;
            }

            .heading-label {
                font-variant: all-petite-caps;
            }
        `,
    ];

    @property({ type: Boolean }) noHeader = false;
    @property({ type: String }) label: string | undefined = undefined;
    @property({ type: String }) direction1 = "N";
    @property({ type: String }) direction2 = "S";

    @state() protected _degrees: number | undefined;
    @state() protected _minutes: number | undefined;
    @state() protected _seconds: number | undefined;
    @state() protected _direction = 1;

    @watch("_degrees")
    @watch("_minutes")
    @watch("_seconds")
    @watch("_direction")
    public entryChanged() {
        if (this._degrees && this._minutes && this._seconds) {
            this.dispatchEvent(
                makeCustomEvent("ds-changed", {
                    dms: {
                        degrees: this._degrees * this._direction,
                        minutes: this._minutes,
                        seconds: this._seconds,
                    } as DegreesMinutesSeconds,

                    decimal: dmsToDecimalDegrees(
                        this._degrees * this._direction,
                        this._minutes,
                        this._seconds
                    ),
                })
            );
        } else {
            this.dispatchEvent(makeCustomEvent("ds-error"));
        }
    }

    private _renderEntry(label: string, value: number | undefined, onChange: any) {
        return html`
            <div class="entry">
                ${litIf(!this.noHeader, () => html`<span class="heading-label">${label}</span>`)}
                <sl-input
                    size="small"
                    type="number"
                    .value=${value?.toString() ?? ""}
                    @sl-input=${(e: Event) => onChange(e)}
                ></sl-input>
            </div>
        `;
    }

    override render() {
        return html`<div
            class="container"
            @sl-hide=${(e: Event) => e.stopPropagation()}
            @sl-after-hide=${(e: Event) => e.stopPropagation()}
        >
            ${litIf(
                !!this.label,
                () =>
                    html`<div class="entry label">
                        ${litIf(!this.noHeader, () => html`<span>&nbsp;</span>`)}<span
                            >${this.label}</span
                        >
                    </div>`
            )}
            ${this._renderEntry(
                "Degs",
                this._degrees,
                (e: Event) => (this._degrees = getInputValueAsNumber(e.target as HTMLInputElement))
            )}
            ${this._renderEntry(
                "Mins",
                this._minutes,
                (e: Event) => (this._minutes = getInputValueAsNumber(e.target as HTMLInputElement))
            )}
            ${this._renderEntry(
                "Secs",
                this._seconds,
                (e: Event) => (this._seconds = getInputValueAsNumber(e.target as HTMLInputElement))
            )}
            <div class="entry">
                ${litIf(!this.noHeader, () => html`<span>&nbsp;</span>`)}
                <sl-select
                    hoist
                    size="small"
                    pill
                    value="one"
                    @sl-change=${(e: any) => {
                        if (e.target.value === "one") {
                            this._direction = 1;
                        } else {
                            this._direction = -1;
                        }
                    }}
                >
                    <sl-option value="one">${this.direction1}</sl-option>
                    <sl-option value="minus-one">${this.direction2}</sl-option>
                </sl-select>
            </div>
        </div>`;
    }
}

@customElement("ds-dms-to-decimal-degrees")
export class DmsToDecimalDegrees extends BaseElement {
    static override styles = [Style, css``];

    @state() protected _lat: number | undefined;
    @state() protected _lon: number | undefined;

    public reset() {
        this._lat = this._lon = undefined;
    }

    @watch("_lat")
    @watch("_lon")
    public _changed() {
        if (this._lat && this._lon) {
            this.dispatchEvent(
                makeCustomEvent("ds-changed", {
                    location: {
                        lat: this._lat,
                        lon: this._lon,
                    } as ILocation,
                })
            );
        } else {
            this.dispatchEvent(makeCustomEvent("ds-error"));
        }
    }

    override render() {
        return html`<div class="flex-column">
            <ds-dms-entry
                label="Lat"
                @ds-changed=${(e: CustomEvent) => {
                    this._lat = e.detail.decimal;
                    e.stopPropagation();
                }}
                @ds-error=${() => (this._lat = undefined)}
            ></ds-dms-entry>
            <ds-dms-entry
                noHeader
                label="Lon"
                direction1="E"
                direction2="W"
                @ds-changed=${(e: CustomEvent) => {
                    this._lon = e.detail.decimal;
                    e.stopPropagation();
                }}
                @ds-error=${() => (this._lon = undefined)}
            ></ds-dms-entry>
        </div>`;
    }
}

@customElement("ds-dms-to-decimal-degrees-dialog")
export class DmsToDecimalDegreesDialog extends BaseElement {
    static override styles = [Style, css``];

    @property({ type: Boolean }) open = false;

    @state() protected _location: ILocation | undefined;

    @query("#dialog") _dialog: SlDialog | undefined;

    public show() {
        this._dialog?.show();
    }

    private hide() {
        this._dialog?.hide();
    }

    protected override firstUpdated(): void {
        if (this.open) {
            this.show();
        }
    }
    protected cancel() {
        this.dispatchEvent(
            new CustomEvent("on-cancel", {
                bubbles: true,
                composed: true,
                detail: {},
            })
        );
        this.hide();
    }

    override render() {
        return html`<sl-dialog id="dialog" label="Enter degrees, minutes and seconds">
            <ds-dms-to-decimal-degrees
                @ds-changed=${(e: CustomEvent) => (this._location = e.detail.location)}
                @ds-error=${() => (this._location = undefined)}
            >
            </ds-dms-to-decimal-degrees>
            ${litIf(
                !this._location,
                () => html`<p style="color:var(--sl-color-danger-400)">Invalid coordinates</p>`,
                () => html`<p>Lat: ${this._location?.lat} Lon: ${this._location?.lon}</p>`
            )}
            <div slot="footer">
                <sl-button variant="primary" @click=${() => this.cancel()}>Cancel</sl-button>
                <sl-button
                    variant="primary"
                    ?disabled=${!this._location}
                    @click=${() => {
                        this.dispatchEvent(
                            makeCustomEvent("on-approved", { location: this._location })
                        );
                        this.hide();
                    }}
                >
                    Accept</sl-button
                >
            </div>
        </sl-dialog>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-dms-to-decimal-degrees": DmsToDecimalDegrees;
        "ds-dms-entry": DmsEntry;
        "ds-dms-to-decimal-degrees-dialog": DmsToDecimalDegreesDialog;
    }
}
